import { HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { inject, Injector } from '@angular/core';
import {
  CustomIdGenerator,
  CustomSpanContext,
  getTraceParent,
  OpenTelemetrySpanService,
  TraceFlags,
  getCurrentNanoseconds,
} from '@lentau/core/open-telemetry/shared/open-telemetry.span';
import { PerformanceMetricsService } from '@lentau/core/open-telemetry/shared/performance-metrics/performance-metrics.service';
import { getNetworkRequestDurationFromPerformanceAPI } from '@lentau/core/open-telemetry/shared/performance-metrics/performance-api.utils';
import { CityStoreFacade } from '@lentau/city/store/city.facade';
import { normalizeRegionSlug } from '@lentau/core/open-telemetry/shared/performance-metrics/image-metrics.utils';
import type { Region } from 'angular-api-gateway/v1/model/region';

const idGenerator: CustomIdGenerator = new CustomIdGenerator();

export function openTelemetryInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const injector: Injector = inject(Injector);
  return handleOpenTelemetryInterceptor(req, next, injector);
}

function handleOpenTelemetryInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  injector: Injector
): Observable<HttpEvent<unknown>> {
  const openTelemetrySpanService: OpenTelemetrySpanService = injector.get(OpenTelemetrySpanService);
  const performanceMetricsService: PerformanceMetricsService = injector.get(PerformanceMetricsService);
  const cityFacade: CityStoreFacade = injector.get(CityStoreFacade);

  // Обновляем текущий регион в PerformanceMetricsService
  const city: Region | null = cityFacade.city();
  const regionName: string | null = city?.name ?? null;
  const regionSlug: string | null = regionName ? normalizeRegionSlug(regionName) : cityFacade.citySlug() || null;
  performanceMetricsService.setCurrentRegion(regionName, regionSlug);

  // Генерация кастомных ID
  const customTraceId: string = idGenerator.generateTraceId();
  const customSpanId: string = idGenerator.generateSpanId();
  const traceFlags: number = TraceFlags;

  const customSpanContext: CustomSpanContext = {
    traceId: customTraceId,
    spanId: customSpanId,
    traceFlags: traceFlags,
    isRemote: false,
  };

  // Добавляем заголовки к запросу сразу
  const modifiedReq: HttpRequest<unknown> = request.clone({
    setHeaders: {
      traceparent: getTraceParent(customSpanContext),
      'x-trace-id': customTraceId,
      'x-span-id': customSpanId,
    },
  });

  const startTimeNs: number = getCurrentNanoseconds();
  const startTimeMs: number = performance.now();
  const timeOrigin: number = performance.timeOrigin ?? Date.now() - startTimeMs;
  const startTimeAbsolute: number = timeOrigin + startTimeMs;

  // Размер запроса для метрик производительности
  const requestSize: number = request.body
    ? typeof request.body === 'string'
      ? request.body.length
      : JSON.stringify(request.body).length
    : 0;

  // Начинаем отслеживание запроса
  const trackingIdPromise: Promise<string> = performanceMetricsService.startNetworkTracking(
    request.urlWithParams,
    request.method,
    requestSize,
    startTimeAbsolute
  );

  return next(modifiedReq).pipe(
    tap((event: HttpEvent<unknown>) => {
      if (event instanceof HttpResponse) {
        const endTimeNs: number = getCurrentNanoseconds();
        const endTimeMs: number = performance.now();
        const fallbackDuration: number = endTimeMs - startTimeMs;

        const responseSize: number = event.body
          ? typeof event.body === 'string'
            ? event.body.length
            : JSON.stringify(event.body).length
          : 0;

        queueMicrotask(() => {
          const duration: number = getNetworkRequestDurationFromPerformanceAPI(request.urlWithParams, fallbackDuration);

          const endTimeAbsolute: number = startTimeAbsolute + duration;

          // OpenTelemetry span
          openTelemetrySpanService
            .processSpanAsync('response', event, request, customSpanContext, startTimeNs, endTimeNs)
            .catch(console.warn);

          // Performance metrics с точным временем окончания
          trackingIdPromise.then((trackingId: string) => {
            void performanceMetricsService.completeNetworkTracking(trackingId, {
              responseCode: event.status,
              responseSize,
              duration,
              traceId: customTraceId,
              endTime: endTimeAbsolute,
            });
          });
        });

        // Проверяем связь с отслеживанием экранов (синхронно, так как это быстро)
        performanceMetricsService.handleBlockingRequestCompletion(request.urlWithParams);
      }
    }),
    catchError((error: unknown) => {
      const endTimeNs: number = getCurrentNanoseconds();
      const endTimeMs: number = performance.now();
      const fallbackDuration: number = endTimeMs - startTimeMs;

      queueMicrotask(() => {
        const duration: number = getNetworkRequestDurationFromPerformanceAPI(request.urlWithParams, fallbackDuration);
        const endTimeAbsolute: number = startTimeAbsolute + duration;

        // OpenTelemetry span для ошибки
        const httpError: HttpErrorResponse =
          error instanceof HttpErrorResponse ? error : new HttpErrorResponse({ error });
        openTelemetrySpanService
          .processSpanAsync(
            'error',
            httpError as unknown as HttpResponse<unknown>,
            request,
            customSpanContext,
            startTimeNs,
            endTimeNs
          )
          .catch(console.warn);

        trackingIdPromise.then((trackingId: string) => {
          void performanceMetricsService.completeNetworkTracking(trackingId, {
            responseCode: (error as { status?: number }).status || 0,
            responseSize: 0,
            duration,
            traceId: customTraceId,
            endTime: endTimeAbsolute,
          });
        });
      });

      performanceMetricsService.handleBlockingRequestCompletion(request.urlWithParams);

      return throwError(() => error);
    })
  );
}
